/**
 * UpgradePage Component
 * Handles plan upgrades for existing authenticated users.
 * Flow: Select plan → Stripe Checkout → Profile prompt (if name missing) → Redirect
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Crown, Check, ExternalLink, Loader2, Shield, AlertCircle,
  User, Rocket, Zap, Building2,
} from 'lucide-react';
import { PageLayout } from '@/components';
import { Button, Input, ErrorBanner } from '@/components/ui';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCapabilities } from '@/contexts/CapabilitiesContext';
import { usePlans } from '@/contexts/PlansContext';
import { stripeApi } from '@/services/stripeApi';
import { useTranslation } from 'react-i18next';

/** Map icon name from DB to React component */
const ICON_MAP: Record<string, React.ElementType> = {
  user: User,
  rocket: Rocket,
  zap: Zap,
  building2: Building2,
};

/** Map color theme from DB to Tailwind classes */
const PLAN_STYLES: Record<string, {
  border: string;
  bg: string;
  badge: string;
  icon: string;
  iconBg: string;
  ring: string;
}> = {
  blue: {
    border: 'border-blue-400',
    bg: 'bg-blue-50/50',
    badge: 'bg-blue-100 text-blue-700',
    icon: 'text-blue-600',
    iconBg: 'bg-blue-100',
    ring: 'ring-blue-400',
  },
  amber: {
    border: 'border-amber-400',
    bg: 'bg-amber-50/50',
    badge: 'bg-amber-100 text-amber-700',
    icon: 'text-amber-600',
    iconBg: 'bg-amber-100',
    ring: 'ring-amber-400',
  },
  slate: {
    border: 'border-slate-300',
    bg: 'bg-white',
    badge: 'bg-slate-100 text-slate-700',
    icon: 'text-slate-600',
    iconBg: 'bg-slate-100',
    ring: 'ring-slate-400',
  },
};

export const UpgradePage: React.FC = () => {
  const { t } = useTranslation();
  const { plans, error: plansError, refreshPlans } = usePlans();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, user, updateUserAttributes } = useAuthContext();
  const { planId, isPaidPlan, isProPlan, refreshCapabilities } = useCapabilities();

  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'pro' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [fullName, setFullName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Check for success return from Stripe
  const sessionId = searchParams.get('session_id');

  // Filter to paid plans only (exclude free and organization)
  const paidPlans = plans.filter(p => p.planId === 'starter' || p.planId === 'pro');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/signup');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Handle successful return from Stripe Checkout
  useEffect(() => {
    if (sessionId && !showSuccess) {
      setShowSuccess(true);
      const upgradedPlan = searchParams.get('plan') || 'pro';

      refreshCapabilities().then(() => {
        updateUserAttributes({ plan: upgradedPlan }).then(() => {
          if (!user?.fullName) {
            setShowProfilePrompt(true);
          } else {
            setTimeout(() => navigate('/add'), 3000);
          }
        });
      });
    }
  }, [sessionId, showSuccess, refreshCapabilities, navigate, updateUserAttributes, user?.fullName, searchParams]);

  // Redirect if already on a paid plan (and not showing success)
  useEffect(() => {
    if (!authLoading && isProPlan && !sessionId) {
      navigate('/add');
    }
  }, [authLoading, isProPlan, sessionId, navigate]);

  // Handle upgrade click - redirect to Stripe Checkout
  const handleUpgrade = useCallback(async () => {
    if (!user?.email || !selectedPlan) return;

    setError(null);
    setIsLoading(true);

    try {
      const { url } = await stripeApi.createCheckoutSession({
        email: user.email,
        plan: selectedPlan,
        successUrl: `${window.location.origin}/upgrade?session_id={CHECKOUT_SESSION_ID}&plan=${selectedPlan}`,
        cancelUrl: `${window.location.origin}/upgrade`,
      });

      window.location.href = url;
    } catch (err) {
      setIsLoading(false);
      const message = err instanceof Error ? err.message : 'Failed to start checkout';
      setError(message);
    }
  }, [user, selectedPlan]);

  // Handle profile save
  const handleProfileSave = useCallback(async () => {
    if (!fullName.trim()) return;
    setIsSavingProfile(true);
    try {
      await updateUserAttributes({ fullName: fullName.trim() });
      navigate('/add');
    } catch {
      navigate('/add');
    }
  }, [fullName, updateUserAttributes, navigate]);

  // Loading state
  if (authLoading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </PageLayout>
    );
  }

  // Success state with optional profile prompt
  if (showSuccess) {
    const upgradedPlanData = plans.find(p => p.planId === (searchParams.get('plan') || 'pro'));
    const planLabel = upgradedPlanData?.displayName || 'Pro';

    return (
      <PageLayout>
        <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-20 pb-12 px-4 sm:pt-24">
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Welcome to {planLabel}!
              </h2>
              <p className="text-slate-600 mb-6">
                Your upgrade is complete. All {planLabel} features are now unlocked.
              </p>

              {showProfilePrompt ? (
                <div className="space-y-4 mt-2">
                  <p className="text-sm text-slate-500">One quick thing — what should we call you?</p>
                  <Input
                    type="text"
                    value={fullName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFullName(e.target.value)}
                    leftIcon={<User className="w-5 h-5" />}
                    variant="emerald"
                    inputSize="lg"
                    placeholder="Your name"
                    autoFocus
                  />
                  <Button
                    onClick={handleProfileSave}
                    disabled={!fullName.trim() || isSavingProfile}
                    variant="gradient-emerald"
                    size="lg"
                    shape="rounded"
                    fullWidth
                  >
                    {isSavingProfile ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      'Get Started'
                    )}
                  </Button>
                  <button
                    onClick={() => navigate('/add')}
                    className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Skip for now
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-emerald-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('upgrade.redirectingToRecord')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pt-20 pb-12 px-4 sm:pt-24">
        <div className="max-w-2xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
              <Crown className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {t('upgrade.title')}
            </h1>
            <p className="text-slate-600">
              {t('upgrade.subtitle')}
            </p>
          </div>

          {/* Current plan indicator */}
          <div className="bg-slate-100 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm text-slate-600">
              {t('upgrade.currentPlan')} <span className="font-semibold capitalize">{planId}</span>
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          {/* Plans Error */}
          {plansError && (
            <ErrorBanner message={plansError} onRetry={refreshPlans} className="mb-6" />
          )}

          {/* Plan Cards */}
          {!plansError && paidPlans.length > 0 && <div className="grid md:grid-cols-2 gap-6 mb-8">
            {paidPlans.map((plan) => {
              const isSelected = selectedPlan === plan.planId;
              const style = PLAN_STYLES[plan.display?.colorTheme] || PLAN_STYLES.slate;
              const Icon = ICON_MAP[plan.display?.icon] || Rocket;

              return (
                <button
                  key={plan.planId}
                  onClick={() => setSelectedPlan(plan.planId as 'starter' | 'pro')}
                  className={`text-left rounded-2xl p-6 border-2 transition-all ${
                    isSelected
                      ? `${style.border} ${style.bg} shadow-lg ring-2 ring-offset-2 ${style.ring}`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md'
                  }`}
                >
                  {/* Plan header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${style.iconBg}`}>
                      <Icon className={`w-5 h-5 ${style.icon}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{plan.displayName}</h3>
                      <p className="text-sm text-slate-500">{plan.description}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-4">
                    <p className="text-sm text-slate-500">{t('pricing.priceShownAtCheckout')}</p>
                  </div>

                  {/* Recommended badge */}
                  {plan.display?.recommended && (
                    <div className="mb-4">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${style.badge}`}>
                        {t('pricing.mostPopular')}
                      </span>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-2">
                    {plan.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="mt-4 flex items-center gap-2 text-emerald-600 font-medium text-sm">
                      <Check className="w-4 h-4" />
                      Selected
                    </div>
                  )}
                </button>
              );
            })}
          </div>}

          {/* Security Notice */}
          <div className="flex items-center gap-3 p-3 mb-6 bg-slate-50 rounded-xl">
            <Shield className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <p className="text-xs text-slate-600">
              {t('pricing.stripeSecurityNotice')}
            </p>
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleUpgrade}
            disabled={isLoading || !selectedPlan}
            variant="gradient-emerald"
            size="lg"
            shape="rounded"
            className="w-full"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('common.redirectingToCheckout')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {selectedPlan ? `Upgrade to ${paidPlans.find(p => p.planId === selectedPlan)?.displayName || 'Plan'}` : 'Select a plan to continue'}
                {selectedPlan && <ExternalLink className="w-4 h-4" />}
              </span>
            )}
          </Button>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-slate-500">
            <p>{t('pricing.cancelAnytime')}</p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default UpgradePage;
